/**
 * EARS (Easy Approach to Requirements Syntax) 解析器
 * 用于解析和生成符合 EARS 语法的需求文档
 */

export interface EARSRequirement {
  id: string;
  type: EARSType;
  trigger?: string;
  when?: string;
  where?: string;
  while?: string;
  if?: string;
  system: string;
  shall: string;
  response?: string;
  priority?: 'must' | 'should' | 'could';
  rationale?: string;
  tags?: string[];
  rawText?: string;
}

export enum EARSType {
  UBIQUITOUS = 'ubiquitous',    // 普遍性需求
  EVENT_DRIVEN = 'event-driven', // 事件驱动需求
  STATE_DRIVEN = 'state-driven', // 状态驱动需求
  OPTIONAL = 'optional',         // 可选功能需求
  COMPLEX = 'complex',           // 复杂需求
  UNWANTED = 'unwanted'          // 不想要的行为
}

export class EARSParser {
  private patterns = {
    // The <system> shall <response>
    ubiquitous: /^(?:The\s+)?(?<system>[\w\s]+)\s+shall\s+(?<response>.+)$/i,
    
    // WHEN <trigger> the <system> shall <response>
    eventDriven: /^WHEN\s+(?<trigger>.+?)\s+the\s+(?<system>[\w\s]+)\s+shall\s+(?<response>.+)$/i,
    
    // WHILE <state> the <system> shall <response>
    stateDriven: /^WHILE\s+(?<state>.+?)\s+the\s+(?<system>[\w\s]+)\s+shall\s+(?<response>.+)$/i,
    
    // WHERE <location> the <system> shall <response>
    locationDriven: /^WHERE\s+(?<location>.+?)\s+the\s+(?<system>[\w\s]+)\s+shall\s+(?<response>.+)$/i,
    
    // IF <condition> THEN the <system> shall <response>
    optional: /^IF\s+(?<condition>.+?)\s+THEN\s+the\s+(?<system>[\w\s]+)\s+shall\s+(?<response>.+)$/i,
    
    // Complex: WHEN <trigger> WHERE <location> WHILE <state> IF <condition> the <system> shall <response>
    complex: /^(?:WHEN\s+(?<trigger>.+?)\s+)?(?:WHERE\s+(?<location>.+?)\s+)?(?:WHILE\s+(?<state>.+?)\s+)?(?:IF\s+(?<condition>.+?)\s+THEN\s+)?the\s+(?<system>[\w\s]+)\s+shall\s+(?<response>.+)$/i,
    
    // The <system> shall NOT <response>
    unwanted: /^(?:The\s+)?(?<system>[\w\s]+)\s+shall\s+NOT\s+(?<response>.+)$/i,
  };
  
  /**
   * 解析单个需求语句
   */
  parseRequirement(text: string): EARSRequirement | null {
    const cleanText = text.trim();
    
    // 尝试解析不想要的行为
    if (cleanText.includes('shall NOT')) {
      const match = this.patterns.unwanted.exec(cleanText);
      if (match && match.groups) {
        return {
          id: this.generateId(),
          type: EARSType.UNWANTED,
          system: match.groups.system.trim(),
          shall: `NOT ${match.groups.response.trim()}`,
          rawText: cleanText
        };
      }
    }
    
    // 尝试解析复杂需求
    if (cleanText.includes('WHEN') || cleanText.includes('WHERE') || 
        cleanText.includes('WHILE') || cleanText.includes('IF')) {
      const match = this.patterns.complex.exec(cleanText);
      if (match && match.groups) {
        const req: EARSRequirement = {
          id: this.generateId(),
          type: EARSType.COMPLEX,
          system: match.groups.system.trim(),
          shall: match.groups.response.trim(),
          rawText: cleanText
        };
        
        if (match.groups.trigger) req.when = match.groups.trigger.trim();
        if (match.groups.location) req.where = match.groups.location.trim();
        if (match.groups.state) req.while = match.groups.state.trim();
        if (match.groups.condition) req.if = match.groups.condition.trim();
        
        // 确定更具体的类型
        if (req.when && !req.where && !req.while && !req.if) {
          req.type = EARSType.EVENT_DRIVEN;
        } else if (req.while && !req.when && !req.where && !req.if) {
          req.type = EARSType.STATE_DRIVEN;
        } else if (req.if && !req.when && !req.where && !req.while) {
          req.type = EARSType.OPTIONAL;
        }
        
        return req;
      }
    }
    
    // 尝试解析普遍性需求
    const ubiquitousMatch = this.patterns.ubiquitous.exec(cleanText);
    if (ubiquitousMatch && ubiquitousMatch.groups) {
      return {
        id: this.generateId(),
        type: EARSType.UBIQUITOUS,
        system: ubiquitousMatch.groups.system.trim(),
        shall: ubiquitousMatch.groups.response.trim(),
        rawText: cleanText
      };
    }
    
    return null;
  }
  
  /**
   * 解析多个需求
   */
  parseRequirements(text: string): EARSRequirement[] {
    const lines = text.split('\n').filter(line => line.trim());
    const requirements: EARSRequirement[] = [];
    
    for (const line of lines) {
      // 跳过注释和空行
      if (line.startsWith('#') || line.startsWith('//')) continue;
      
      const req = this.parseRequirement(line);
      if (req) {
        requirements.push(req);
      }
    }
    
    return requirements;
  }
  
  /**
   * 生成需求 ID
   */
  private generateId(): string {
    return `REQ-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
  }
}

export class EARSGenerator {
  /**
   * 生成 EARS 格式的需求语句
   */
  generateRequirement(req: EARSRequirement): string {
    let statement = '';
    
    switch (req.type) {
      case EARSType.UBIQUITOUS:
        statement = `The ${req.system} shall ${req.shall}`;
        break;
        
      case EARSType.EVENT_DRIVEN:
        statement = `WHEN ${req.when || req.trigger} the ${req.system} shall ${req.shall}`;
        break;
        
      case EARSType.STATE_DRIVEN:
        statement = `WHILE ${req.while} the ${req.system} shall ${req.shall}`;
        break;
        
      case EARSType.OPTIONAL:
        statement = `IF ${req.if} THEN the ${req.system} shall ${req.shall}`;
        break;
        
      case EARSType.UNWANTED:
        statement = `The ${req.system} shall NOT ${req.shall.replace('NOT ', '')}`;
        break;
        
      case EARSType.COMPLEX:
        const parts: string[] = [];
        if (req.when) parts.push(`WHEN ${req.when}`);
        if (req.where) parts.push(`WHERE ${req.where}`);
        if (req.while) parts.push(`WHILE ${req.while}`);
        if (req.if) parts.push(`IF ${req.if} THEN`);
        parts.push(`the ${req.system} shall ${req.shall}`);
        statement = parts.join(' ');
        break;
    }
    
    return statement;
  }
  
  /**
   * 生成需求文档
   */
  generateDocument(requirements: EARSRequirement[], options?: {
    title?: string;
    includeMetadata?: boolean;
    groupByType?: boolean;
  }): string {
    const lines: string[] = [];
    const opts = {
      title: 'Requirements Document',
      includeMetadata: true,
      groupByType: true,
      ...options
    };
    
    // 标题
    lines.push(`# ${opts.title}`);
    lines.push('');
    lines.push(`Generated: ${new Date().toISOString()}`);
    lines.push(`Total Requirements: ${requirements.length}`);
    lines.push('');
    
    if (opts.groupByType) {
      // 按类型分组
      const groups = this.groupByType(requirements);
      
      for (const [type, reqs] of Object.entries(groups)) {
        if (reqs.length === 0) continue;
        
        lines.push(`## ${this.getTypeTitle(type as EARSType)}`);
        lines.push('');
        
        for (const req of reqs) {
          if (opts.includeMetadata) {
            lines.push(`**[${req.id}]** ${req.priority ? `(${req.priority})` : ''}`);
          }
          lines.push(this.generateRequirement(req));
          if (req.rationale) {
            lines.push(`> Rationale: ${req.rationale}`);
          }
          if (req.tags && req.tags.length > 0) {
            lines.push(`> Tags: ${req.tags.join(', ')}`);
          }
          lines.push('');
        }
      }
    } else {
      // 顺序列出
      for (const req of requirements) {
        if (opts.includeMetadata) {
          lines.push(`**[${req.id}]** ${req.priority ? `(${req.priority})` : ''}`);
        }
        lines.push(this.generateRequirement(req));
        if (req.rationale) {
          lines.push(`> Rationale: ${req.rationale}`);
        }
        lines.push('');
      }
    }
    
    return lines.join('\n');
  }
  
  /**
   * 按类型分组
   */
  private groupByType(requirements: EARSRequirement[]): Record<EARSType, EARSRequirement[]> {
    const groups: Record<EARSType, EARSRequirement[]> = {
      [EARSType.UBIQUITOUS]: [],
      [EARSType.EVENT_DRIVEN]: [],
      [EARSType.STATE_DRIVEN]: [],
      [EARSType.OPTIONAL]: [],
      [EARSType.COMPLEX]: [],
      [EARSType.UNWANTED]: []
    };
    
    for (const req of requirements) {
      groups[req.type].push(req);
    }
    
    return groups;
  }
  
  /**
   * 获取类型标题
   */
  private getTypeTitle(type: EARSType): string {
    const titles = {
      [EARSType.UBIQUITOUS]: 'Ubiquitous Requirements (普遍性需求)',
      [EARSType.EVENT_DRIVEN]: 'Event-Driven Requirements (事件驱动需求)',
      [EARSType.STATE_DRIVEN]: 'State-Driven Requirements (状态驱动需求)',
      [EARSType.OPTIONAL]: 'Optional Feature Requirements (可选功能需求)',
      [EARSType.COMPLEX]: 'Complex Requirements (复杂需求)',
      [EARSType.UNWANTED]: 'Unwanted Behaviors (不想要的行为)'
    };
    
    return titles[type] || type;
  }
}

/**
 * EARS 模板生成器
 */
export class EARSTemplateGenerator {
  /**
   * 生成 EARS 需求模板
   */
  static generateTemplate(type: EARSType, context?: {
    system?: string;
    feature?: string;
  }): string {
    const system = context?.system || '<system name>';
    const feature = context?.feature || '<feature>';
    
    const templates = {
      [EARSType.UBIQUITOUS]: `The ${system} shall ${feature}`,
      [EARSType.EVENT_DRIVEN]: `WHEN <trigger event> the ${system} shall <response>`,
      [EARSType.STATE_DRIVEN]: `WHILE <in state> the ${system} shall <response>`,
      [EARSType.OPTIONAL]: `IF <optional condition> THEN the ${system} shall <response>`,
      [EARSType.COMPLEX]: `WHEN <trigger> WHERE <location> WHILE <state> IF <condition> the ${system} shall <response>`,
      [EARSType.UNWANTED]: `The ${system} shall NOT <unwanted behavior>`
    };
    
    return templates[type];
  }
  
  /**
   * 生成示例需求
   */
  static generateExamples(): EARSRequirement[] {
    return [
      {
        id: 'REQ-001',
        type: EARSType.UBIQUITOUS,
        system: 'user authentication system',
        shall: 'encrypt all passwords using bcrypt with a minimum of 10 rounds',
        priority: 'must',
        tags: ['security', 'authentication']
      },
      {
        id: 'REQ-002',
        type: EARSType.EVENT_DRIVEN,
        system: 'system',
        shall: 'send a confirmation email to the user',
        when: 'a new user registers',
        priority: 'must',
        tags: ['user-registration', 'email']
      },
      {
        id: 'REQ-003',
        type: EARSType.STATE_DRIVEN,
        system: 'shopping cart',
        shall: 'display the total price including taxes',
        while: 'items are in the cart',
        priority: 'must',
        tags: ['shopping-cart', 'pricing']
      },
      {
        id: 'REQ-004',
        type: EARSType.OPTIONAL,
        system: 'application',
        shall: 'use dark mode for the UI',
        if: 'the user has enabled dark mode in settings',
        priority: 'should',
        tags: ['ui', 'preferences']
      },
      {
        id: 'REQ-005',
        type: EARSType.UNWANTED,
        system: 'API',
        shall: 'NOT expose internal error details in responses',
        priority: 'must',
        rationale: 'Prevents information leakage that could be exploited by attackers',
        tags: ['security', 'api']
      }
    ];
  }
}